import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPriceWithUSD, getExchangeRate } from '../utils/currencyConverter';
import { usePreferredCurrency } from './CurrencyToggle';

interface PriceDisplayProps {
  priceMXN?: number;
  priceUSD?: number;
  className?: string;
  showBoth?: boolean;
  showExchangeRate?: boolean;
}

export default function PriceDisplay({
  priceMXN,
  priceUSD,
  className = "",
  showBoth = false,
  showExchangeRate = false
}: PriceDisplayProps) {
  const { t } = useTranslation();
  const preferredCurrency = usePreferredCurrency();
  const [prices, setPrices] = useState<{
    mxn: string;
    usd: string;
    usdAmount: number;
    mxnAmount: number;
  } | null>(null);
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const convertPrice = async () => {
      try {
        // Validar que tengamos al menos un precio
        if ((!priceMXN || isNaN(priceMXN) || priceMXN <= 0) &&
            (!priceUSD || isNaN(priceUSD) || priceUSD <= 0)) {
          setPrices({
            mxn: t('currency.priceNotAvailable'),
            usd: 'N/A',
            usdAmount: 0,
            mxnAmount: 0
          });
          setLoading(false);
          return;
        }

        const rate = await getExchangeRate();
        setExchangeRate(rate);

        let mxnAmount = priceMXN || 0;
        let usdAmount = priceUSD || 0;

        // Si tenemos USD pero no MXN, calculamos MXN
        if (priceUSD && priceUSD > 0 && (!priceMXN || priceMXN === 0)) {
          mxnAmount = Math.round(priceUSD * rate);
        }

        // Si tenemos MXN pero no USD, calculamos USD
        if (priceMXN && priceMXN > 0 && (!priceUSD || priceUSD === 0)) {
          usdAmount = Math.round((priceMXN / rate) * 100) / 100;
        }

        setPrices({
          mxn: `$${mxnAmount.toLocaleString('es-MX')} MXN`,
          usd: `$${usdAmount.toLocaleString('en-US')} USD`,
          usdAmount,
          mxnAmount
        });
      } catch (error) {
        setPrices({
          mxn: `$${(priceMXN || 0).toLocaleString('es-MX')} MXN`,
          usd: priceUSD ? `$${priceUSD.toLocaleString('en-US')} USD` : 'Error USD',
          usdAmount: priceUSD || 0,
          mxnAmount: priceMXN || 0
        });
      } finally {
        setLoading(false);
      }
    };

    convertPrice();
  }, [priceMXN, priceUSD]);

  if (loading) {
    return (
      <div className={`${className} d-flex align-items-center`}>
        <div className="spinner-border spinner-border-sm me-2" role="status">
          <span className="visually-hidden">{t('currency.calculating')}</span>
        </div>
        <span className="text-muted">{t('currency.calculating')}</span>
      </div>
    );
  }

  if (!prices) {
    const safePrice = priceMXN && !isNaN(priceMXN) ? priceMXN : 0;
    return <span className={className}>${safePrice.toLocaleString('es-MX')}</span>;
  }

  // Mostrar ambas monedas
  if (showBoth) {
    return (
      <div className={className}>
        <div className="fw-bold text-primary">{prices.mxn}</div>
        <div className="text-muted small">{prices.usd}</div>
        {showExchangeRate && exchangeRate && (
          <div className="text-muted" style={{ fontSize: '0.7rem' }}>
            <i className="fa fa-info-circle me-1"></i>
            {t('currency.exchangeRate')}: 1 USD = {exchangeRate.toFixed(2)} MXN
          </div>
        )}
      </div>
    );
  }

  // Mostrar solo la moneda preferida
  const displayPrice = preferredCurrency === 'MXN' ? prices.mxn : prices.usd;

  return (
    <div className={className}>
      <span className="fw-bold text-primary">{displayPrice}</span>
      {showExchangeRate && exchangeRate && (
        <div className="text-muted" style={{ fontSize: '0.7rem', marginTop: '2px' }}>
          <i className="fa fa-info-circle me-1"></i>
          {t('currency.exchangeRate')}: 1 USD = {exchangeRate.toFixed(2)} MXN
        </div>
      )}
    </div>
  );
}